import { z } from 'zod';

import type { TFunction } from 'i18next';

import { amountToCents } from '@/helpers';

export const ORDER_REFUND_REGISTER_TARGET = 'cash_register';
export const ORDER_REFUND_DEPOSIT_TARGET = 'deposit';
export const ORDER_REFUND_ORIGINAL_TARGET = 'original_payment';

export const ORDER_REFUND_FULL_MODE = 'full';
export const ORDER_REFUND_PARTIAL_MODE = 'partial';

const refundOrderItemSchema = z.object({
  purchase_type: z.string(),
  purchase_id: z.number(),
  is_selected: z.boolean(),
  is_amount_editable: z.boolean(),
  amount: z.string(),
  max_amount_cents: z.number(),
});

type RefundOrderItemValues = z.infer<typeof refundOrderItemSchema>;

type RefundModeValues = {
  mode: typeof ORDER_REFUND_FULL_MODE | typeof ORDER_REFUND_PARTIAL_MODE;
  items: RefundOrderItemValues[];
};

const isAmountWithin = (amount: string, maxCents: number) => {
  const cents = amountToCents(amount);

  return cents !== null && cents >= 0 && cents <= maxCents;
};

const refineCashRegister =
  (t: TFunction) =>
  (
    values: { refund_target: string; cash_register_id: string },
    ctx: z.RefinementCtx,
  ) => {
    if (
      values.refund_target === ORDER_REFUND_REGISTER_TARGET &&
      values.cash_register_id === ''
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t('validation_required'),
        path: ['cash_register_id'],
      });
    }
  };

const refineItemSelection =
  (t: TFunction) => (values: RefundModeValues, ctx: z.RefinementCtx) => {
    if (
      values.mode === ORDER_REFUND_PARTIAL_MODE &&
      !values.items.some((item) => item.is_selected)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t('Choose at least one item to refund'),
        path: ['items'],
      });
    }
  };

const refineItemAmounts =
  (t: TFunction) => (values: RefundModeValues, ctx: z.RefinementCtx) => {
    if (values.mode !== ORDER_REFUND_PARTIAL_MODE) {
      return;
    }

    values.items.forEach((item, index) => {
      if (
        item.is_selected &&
        item.is_amount_editable &&
        !isAmountWithin(item.amount, item.max_amount_cents)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: t('Enter an amount from 0 to the purchase price'),
          path: ['items', index, 'amount'],
        });
      }
    });
  };

export function createRefundSalesOrderFormSchema(t: TFunction) {
  return z
    .object({
      mode: z.enum([ORDER_REFUND_FULL_MODE, ORDER_REFUND_PARTIAL_MODE]),
      refund_target: z.enum([
        ORDER_REFUND_REGISTER_TARGET,
        ORDER_REFUND_DEPOSIT_TARGET,
        ORDER_REFUND_ORIGINAL_TARGET,
      ]),
      cash_register_id: z.string(),
      refund_reason_id: z
        .string()
        .min(1, { message: t('validation_required') }),
      description: z.string(),
      items: z.array(refundOrderItemSchema),
    })
    .superRefine(refineCashRegister(t))
    .superRefine(refineItemSelection(t))
    .superRefine(refineItemAmounts(t));
}

export type RefundSalesOrderFormValues = z.infer<
  ReturnType<typeof createRefundSalesOrderFormSchema>
>;
